import { useQuery } from '@apollo/client'
import * as React from 'react'
import styled from 'styled-components'
import Icon, { convoTypeToIcon, timeToIconName } from '../../../../common/src/components/Icon'
import { Link } from '../../../../common/src/components/Link'
import { Spacer, InputMed, IntroText, BodyText, H3, BodyTextSpan } from '../../../../common/src/components/core'
import { TestIds } from '../../../../common/src/data/testids'
import { dateTimeTzFmt } from '../../../../common/src/date'
import { formatShortUrl } from '../../../../common/src/format'
import keyboard, { KeyAction } from '../../../../common/src/keyboard'
import {
  FetchThreadQuery,
  FetchInboxQuery,
  FetchTasksPageQuery,
  ConversationType,
  JobName,
  FetchTaskAgentsQuery,
  FetchTaskAgentsQueryVariables,
  FetchUserContextQuery,
} from '../../../../common/src/types.gen'
import { updateFirm, updateTask, runAdminTaskUncaught } from '../../graphql/mutations'
import ActionLog from '../components/ActionLog'
import JobsHistorySection from '../components/JobsHistorySection'
import LeasingSpecialsSection from '../components/LeasingSpecialsSection'
import NotesSection from '../components/NotesSection'
import { ContactText, getContactText } from '../components/contact'
import { ScrollPane } from '../components/containers'
import { TaskLink } from '../components/task'
import { getThreadPathFor } from '../route'
import { taskAgentsQuery } from '../tasks/AgentsSection'
import CalView from './CalView'
import ContactEditBoxes from './ContactEditBoxes'
import ContactTaskLinks from './ContactTaskLinks'
import ContactThreadLinks from './ContactThreadLinks'
import ContextEditBoxes from './ContextEditBoxes'
import NextShowings from './NextShowingsSection'
import ShowingsList from './ShowingsList'

interface ContextSidebarProps {
  user: FetchUserContextQuery['self']
  contactIcon?: any
  thread?: FetchThreadQuery['thread']
  task:
    | NonNullable<FetchInboxQuery['thread']>['leasingTask']
    | NonNullable<FetchTasksPageQuery['task']>
    | NonNullable<FetchTasksPageQuery['thread']>['leasingTask']
}

interface ContextSidebarState {
  now: Date
  showingFilterText: string
}

class ContextSidebar extends React.PureComponent<ContextSidebarProps, ContextSidebarState> {
  state = {
    now: new Date(),
    showingFilterText: '',
  }

  private filterInputRef = React.createRef<HTMLInputElement>()
  private showingsSectionRef = React.createRef<HTMLElement>()
  private interval?: number

  componentDidMount() {
    this.interval = window.setInterval(() => this.setState({ now: new Date() }), 10 * 1000)
    keyboard.addListener(this.onKeyboard)
  }

  componentWillUnmount() {
    window.clearInterval(this.interval)
    keyboard.removeListener(this.onKeyboard)
  }

  render() {
    const { thread, task, contactIcon } = this.props

    const sections = []
    if (thread == null) {
      sections.push(this.renderTaskNotes(false))
      sections.push(this.renderFirmNotes(task.taskType !== 'LEASING_FIRM', false))
      sections.push(this.renderShowings())
    } else {
      sections.push(this.renderContactNameAddrsNotes(thread, contactIcon))
      sections.push(this.renderNextShowing(thread))
      sections.push(this.renderTasksThreadsCalendar(thread))
      sections.push(this.renderTaskNotes(true))
      sections.push(this.renderFirmNotes(task.taskType !== 'LEASING_FIRM', true))
      sections.push(this.renderJobs(thread))
      sections.push(this.renderShowings())
      sections.push(this.renderActionLog(thread))
    }

    return (
      <ScrollPane data-testid={TestIds.contextSidebar}>
        <SectionsList>{sections.filter(s => s != null)}</SectionsList>
      </ScrollPane>
    )
  }

  private onKeyboard = (action: KeyAction) => {
    if (!this.filterInputRef.current) {
      return
    }
    if (action === KeyAction.filterShowings) {
      this.filterInputRef.current.focus()
      this.showingsSectionRef.current!.scrollIntoView()
    } else if (action === KeyAction.esc) {
      this.filterInputRef.current.blur()
    }
  }

  private updateFirmBound = (key: string, value: string) => {
    const { task } = this.props
    const { firm } = task
    if (firm == null) {
      return null
    }
    return updateFirm(firm, key, value)
  }

  private updateTaskBound = (key: string, value: string) => {
    const { task } = this.props
    if (task == null) {
      return null
    }

    return updateTask(task, key, value, true)
  }

  private renderFirmNotes(cascadeChild: boolean, minimal: boolean) {
    const { user, task } = this.props
    if (task == null) {
      return null
    }
    const { firm } = task
    return (
      <Section title={firm.firmName} key="notes-firm">
        {!minimal && (
          <ContextEditBoxes
            cascadeParent={false}
            cascadeChild={cascadeChild}
            leasingTask={task}
            user={user}
            onEdit={this.updateFirmBound}
            type="FIRM"
          />
        )}
        {!minimal && <Spacer height={8} />}
        <NotesSection user={user} firmId={firm.id} />
      </Section>
    )
  }

  private renderTaskNotes(minimal: boolean) {
    const { user, task } = this.props
    const shouldShow = task != null && task.taskType === 'LEASING_PROPERTY'
    if (!shouldShow) {
      return null
    }

    return (
      <Section title={<TaskLink task={task} align="right" iconSize="small" />} key="notes-task">
        <TaskLinks task={task} minimal={minimal} />
        <ContextEditBoxes
          leasingTask={task}
          cascadeParent
          cascadeChild={false}
          user={user}
          onEdit={this.updateTaskBound}
          type={minimal ? 'PROPERTY_MINIMAL' : 'PROPERTY'}
        />
        {!minimal && user.permissions.canViewSuperTaskDetails && (
          <LeasingSpecialsSection
            leasingTask={task}
            user={user}
            onEdit={this.updateTaskBound}
            key="leasing-specials-task"
          />
        )}
        <Spacer height={8} />
        <NotesSection user={user} taskId={task.id} />
      </Section>
    )
  }

  private renderShowings() {
    const { now, showingFilterText } = this.state
    const { task, thread, user } = this.props

    if (task.taskType !== 'LEASING_PROPERTY' || task.address == null) {
      return undefined
    }

    const { tzName } = task.address.geolocation
    const currentTimeElem = currentTime({ now, tzName })

    return (
      <Section title="Showings" key="showings" subtitle={currentTimeElem} innerRef={this.showingsSectionRef}>
        <ShowingFilterInput
          value={showingFilterText}
          onChange={this.onEditShowingFilter}
          placeholder="Filter..."
          ref={this.filterInputRef}
        />
        <Spacer height={16} />
        <ShowingsList task={task} thread={thread} filterText={showingFilterText} user={user} />
      </Section>
    )
  }

  private onEditShowingFilter = (ev: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ showingFilterText: ev.target.value })
  }

  private renderNextShowing(thread: FetchThreadQuery['thread']) {
    const { now } = this.state
    const { task, user } = this.props

    if (task.taskType !== 'LEASING_PROPERTY' || task.address == null) {
      return undefined
    }

    const { tzName } = task.address.geolocation
    const { contactThem } = thread
    const currentTimeElem = currentTime({ now, tzName })

    return (
      <Section title="Next showings" key="next-showings" subtitle={currentTimeElem} innerRef={this.showingsSectionRef}>
        <NextShowings task={task} user={user} contact={contactThem} />
      </Section>
    )
  }

  private renderTasksThreadsCalendar(thread: FetchThreadQuery['thread']) {
    const isClient = ['AGENT', 'BOSS', 'INVESTOR'].includes(thread.conversationType)
    return (
      <Section key="tasks-threads-calendar">
        {isClient && <ContactTaskLinks contactId={thread.contactThem.id} />}
        {isClient && <CalView thread={thread} />}
        {!isClient && <ContactThreadLinks threadId={thread.id} contactId={thread.contactThem.id} />}
      </Section>
    )
  }
  private renderContactNameAddrsNotes(thread: FetchThreadQuery['thread'], icon?: any) {
    const { user } = this.props
    const { contactThem } = thread
    return (
      <Section title={getContactText(contactThem)} subtitle={icon} key="notes">
        <ContactEditBoxes thread={thread} />
        <NotesSection user={user} contactId={contactThem.id} />
      </Section>
    )
  }

  /**
   * Jobs are async processes we run to sync with external systems, like property management software (PmSyncService)
   * or Outlook/Google calendars (CalSyncService). Senior Operators and admins use the Jobs section to force new syncs
   * and see sync results.
   */
  private renderJobs(thread: FetchThreadQuery['thread']) {
    const { user } = this.props
    const syncButtons = []

    if (!user.permissions.canRunBasicJobs) {
      return undefined
    }

    if (thread.conversationType === ConversationType.PROSPECT || thread.conversationType === ConversationType.OTHER) {
      // if on a PROSPECT or OTHER thread, allow for guest card sync to PM system
      syncButtons.push({
        label: 'Save Guest Card',
        handler: () => runAdminTaskUncaught({ name: JobName.ADMIN_SAVE_GUESTCARD, threadId: thread.id }),
      })
    }

    // if you're on an AGENT thread, show buttons to fetch cal
    if (thread.conversationType === ConversationType.AGENT) {
      syncButtons.push({
        label: 'Fetch Calendar',
        handler: () => runAdminTaskUncaught({ name: JobName.ADMIN_FETCH_SINGLE_CALENDAR, threadId: thread.id }),
      })
    }

    return (
      <Section key="sync-jobs" title={'Jobs'}>
        <JobsHistorySection jobKey={JSON.stringify({ threadId: thread.id })} buttons={syncButtons} compact />
      </Section>
    )
  }

  private renderActionLog(thread: FetchThreadQuery['thread']) {
    const {
      contactThem: { id },
      conversationType,
    } = thread

    if (conversationType === ConversationType.AGENT || conversationType === ConversationType.BOSS) {
      return (
        <ActionLogContainer key="action-log">
          <ActionLog contactId={id} />
        </ActionLogContainer>
      )
    }
    return undefined
  }
}

const ActionLogContainer = styled.div`
  padding-left: 16px;
`

const ShowingFilterInput = styled(InputMed)`
  width: 100%;
`

const pelletStyle = { display: 'inline-block', marginRight: '16px' }

interface TaskLinksProps {
  task: NonNullable<FetchInboxQuery['thread']>['leasingTask'] | NonNullable<FetchTasksPageQuery['task']>
  minimal: boolean
}

function TaskLinks(props: TaskLinksProps) {
  const { task, minimal } = props

  const res = useQuery<FetchTaskAgentsQuery, FetchTaskAgentsQueryVariables>(taskAgentsQuery, {
    variables: { taskId: task.id },
  })
  if (res.loading || res.error || res.data == null) {
    return null
  }
  const taskAgents = res.data.task.taskAgents

  // Only show the lowest-level boss
  const lowestBossNumTasks = Math.min.apply(
    null,
    taskAgents.filter(ta => ta.firmTaskThreadType === 'BOSS').map(ta => ta.numTasks)
  )
  const tas = taskAgents.filter(
    ta => 'AGENT' === ta.firmTaskThreadType || ('BOSS' === ta.firmTaskThreadType && ta.numTasks === lowestBossNumTasks)
  )

  const elems = tas.map(ta => {
    const isCurrentlyWorkingOrUnknown = ta.isCurrentlyWorking || ta.isCurrentlyWorking == null
    return (
      <IntroText key={ta.id} style={pelletStyle}>
        <Link
          to={getThreadPathFor({ contactId: ta.agent.id, taskId: task.firmTaskId || 0 })}
          color={isCurrentlyWorkingOrUnknown ? 'silver' : 'midway'}
        >
          {convoTypeToIcon(ta.firmTaskThreadType)}
          <Spacer width={8} />
          <ContactText
            contact={ta.agent}
            short
            tooltip={isCurrentlyWorkingOrUnknown ? undefined : `${getContactText(ta.agent)} is currently not working`}
          />
        </Link>
      </IntroText>
    )
  })
  if (minimal && task.leasingInfo) {
    const { propertyWebsite } = task.leasingInfo
    if (propertyWebsite) {
      elems.push(<Spacer height={8} key={'spacer1'} />)
      elems.push(
        <BodyText key={'propertyWebsite'}>
          <Link href={propertyWebsite}>{formatShortUrl(propertyWebsite, 40)} »</Link>
        </BodyText>
      )
    }
  }
  return (
    <div>
      {elems}
      {elems.length > 0 && <Spacer height={16} />}
    </div>
  )
}

const SectionsList = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 40px;
`

class Section extends React.PureComponent<{
  title?: string | JSX.Element
  subtitle?: JSX.Element
  innerRef?: React.RefObject<any>
}> {
  render() {
    const { title, subtitle, innerRef } = this.props

    return (
      <SectionRoot ref={innerRef}>
        {title && (
          <SectionTitleH3>
            {title}
            {subtitle && <Spacer width={8} />}
            {subtitle}
          </SectionTitleH3>
        )}
        {title && <Spacer height={16} />}
        {this.props.children}
      </SectionRoot>
    )
  }
}

const SectionRoot = styled.div`
  margin: 24px 16px;
`

const SectionTitleH3 = styled(H3)`
  white-space: nowrap;
  width: 0;
  line-height: inherit;
`

export function currentTime({ now, tzName }: { now: Date; tzName: string }) {
  return (
    <BodyTextSpan>
      <br />
      now
      <Spacer width={8} />
      <Icon name={timeToIconName(now, tzName)} />
      <Spacer width={4} />
      {dateTimeTzFmt(now, tzName)}
    </BodyTextSpan>
  )
}

export default ContextSidebar
